import type { Language } from '@/i18n/translations';

/**
 * Map the app's internal Language code to a BCP-47 locale tag suitable
 * for `toLocaleString`, `Intl.DateTimeFormat`, and `Intl.NumberFormat`.
 *
 * Fallback: `sv-SE` (the default UI language) so formatting never throws
 * for an unknown code.
 */
const MAP: Record<Language, string> = {
  sv: 'sv-SE',
  en: 'en-US',
  no: 'nb-NO',
  da: 'da-DK',
  de: 'de-DE',
  fr: 'fr-FR',
  hu: 'hu-HU',
};

export function languageToLocale(lang: Language | string | undefined | null): string {
  if (!lang) return 'sv-SE';
  return MAP[lang as Language] ?? 'sv-SE';
}

/**
 * Format a ratio (e.g. `1.15`) as a locale-aware percentage string
 * (e.g. `115 %` in `sv-SE`, `115%` in `en-US`). `fractionDigits` defaults
 * to 0 — adjust upward for sub-integer precision.
 */
export function formatPercent(
  ratio: number,
  locale: string,
  fractionDigits = 0,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(ratio);
}

/**
 * Format a plain number with locale-aware grouping + decimal separators
 * (e.g. `1234.5` → `1 234,5` in `sv-SE`, `1,234.5` in `en-US`).
 */
export function formatNumber(
  value: number,
  locale: string,
  opts?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, opts).format(value);
}
